// InMemoryStepsModal.jsx
//
// A self-contained steps editor that works entirely in memory — no testCaseId,
// no API calls on save. It mirrors the UI of TestScriptsFormModal but calls
// onSave(steps[]) instead of hitting the DB. The parent (AddTestCaseModal)
// keeps the steps in aiTestSteps state and flushes them to the API only when
// the TC itself is saved.
//
// Props:
//   show            – boolean, controls visibility
//   handleClose     – () => void
//   steps           – Step[] currently held in parent state
//   onSave          – (Step[]) => void  called when user clicks Save
//   technologyLayer – string name (not ID), used to show the URI column

import React, { useEffect, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { Col, Form, Row } from 'react-bootstrap';
import Modal from '../../../elements/Modal';
import TextInput from '../../../elements/TextInput';

const STEP_INITIAL = { step: '', testData: '', expectedResult: '', uri: '' };

const InMemoryStepsModal = ({ show, handleClose, steps, onSave, technologyLayer }) => {
  const [scripts, setScripts] = useState([{ ...STEP_INITIAL }]);

  useEffect(() => {
    if (show) {
      setScripts(
        steps && steps.length > 0
          ? steps.map((s) => ({ ...STEP_INITIAL, ...s }))
          : [{ ...STEP_INITIAL }]
      );
    }
  }, [show, steps]);

  const handleChange = (field, value, index) =>
    setScripts((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));

  const handleAdd = () => setScripts((prev) => [...prev, { ...STEP_INITIAL }]);
  const handleRemove = (index) => setScripts((prev) => prev.filter((_, i) => i !== index));

  const handleSave = () => {
    onSave(scripts);
    handleClose();
  };

  const isSaveDisabled = scripts.some((s) => !s.step.trim());
  const showUri = technologyLayer === 'API/Integration';

  if (!show) return null;

  return (
    <Modal
      open={show}
      width="xl"
      handleClose={handleClose}
      modalTitle="Add / Edit Steps"
      action={() => (
        <>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaveDisabled}
            title={isSaveDisabled ? 'Please fill in all step descriptions' : 'Save'}
          >
            Save
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleClose}>
            Close
          </Button>
        </>
      )}
    >
      <>
        {scripts.map((script, index) => (
          <Row key={index} className="mb-3 align-items-center">
            <Typography variant="subtitle1" color="textSecondary">
              Step {index + 1}
            </Typography>
            <Col>
              <Form.Group>
                <Form.Label className="required">Description</Form.Label>
                <TextInput
                  id="step"
                  type="textarea"
                  rows={4}
                  value={script.step}
                  onChange={(_, val) => handleChange('step', val, index)}
                  placeholder="Enter step description"
                  maxLength={2000}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Test Data</Form.Label>
                <TextInput
                  id="testData"
                  type="textarea"
                  rows={4}
                  value={script.testData}
                  onChange={(_, val) => handleChange('testData', val, index)}
                  placeholder="Enter test data"
                  maxLength={2000}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Expected Result</Form.Label>
                <TextInput
                  id="expectedResult"
                  type="textarea"
                  rows={4}
                  value={script.expectedResult}
                  onChange={(_, val) => handleChange('expectedResult', val, index)}
                  placeholder="Enter expected result"
                  maxLength={2000}
                />
              </Form.Group>
            </Col>
            {showUri && (
              <Col>
                <Form.Group>
                  <Form.Label>URI</Form.Label>
                  <TextInput
                    id="uri"
                    type="textarea"
                    rows={4}
                    value={script.uri}
                    onChange={(_, val) => handleChange('uri', val, index)}
                    placeholder="Enter URI"
                    maxLength={250}
                  />
                </Form.Group>
              </Col>
            )}
            <Row>
              <Col className="d-flex align-items-center justify-content-end">
                {index > 0 && (
                  <Button
                    variant=""
                    size="sm"
                    title="Remove Step"
                    onClick={() => handleRemove(index)}
                    style={{ color: 'red' }}
                  >
                    <Remove /> Remove
                  </Button>
                )}
                {index === scripts.length - 1 && (
                  <Button variant="" size="sm" onClick={handleAdd} title="Add">
                    <Add /> Add New Step
                  </Button>
                )}
              </Col>
            </Row>
          </Row>
        ))}
      </>
    </Modal>
  );
};

export default InMemoryStepsModal;